import React from 'react';
import PropTypes from 'prop-types';
import moment from 'logic/moment';
import { FormattedMessage } from 'react-intl';
import CircularProgress from 'components/shared/CircularProgress';
import FormatPrice from 'components/shared/FormatPrice';
import {
  Divider,
  Paper,
  Typography,
  withStyles,
} from '@material-ui/core';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(4),
  },
  desc: {
    minWidth: 220,
    display: 'inline-block',
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
});

export const orderHasBooking = (order) => {
  return order && order.products && !!order.products.some((op) => op.id === 12 || op.id === 13 || op.id === 14 || op.id === 11);
};

export const orderHasTax = (order) => {
  if (!order) {
    return false;
  }

  return !orderHasBooking(order) || order.products.some((op) => op.id === 11);
};

const Receipt = ({
  classes,
  isLoading,
  order,
}) => {
  if (isLoading) {
    return (<CircularProgress />);
  }

  const hasBooking = orderHasBooking(order);
  const hasTax = orderHasTax(order);

  const amount = order.amount || 0;

  return (
    <Paper className={classes.root}>
      <Typography
        paragraph
        variant="h6"
      >
        {moment(order.created_at).format('L')} | #{order.id}
      </Typography>
      <Typography>
        <span className={classes.desc}>
          {
            hasBooking &&
            (
              <FormattedMessage
                id="Booking"
                defaultMessage="Booking"
              />
            )
          }
          {
            !hasBooking &&
            (
              <FormattedMessage
                id="Cottage"
                defaultMessage="Cottage"
              />
            )
          }

        </span>
        {order.booking_id || order.cottage_id}
      </Typography>
      {order.paid_at && (
        <Typography
          paragraph
        >
          <span className={classes.desc}>
            <FormattedMessage
              id="Date"
              defaultMessage="Date"
            />
          </span>
          {moment(order.paid_at).format('LLL')}
        </Typography>
      )}
      {order.products && order.products.map(({
        discount,
        id,
        name,
        price,
      }) => (
        <Typography
          key={id}
        >
          <span className={classes.desc}>
            {
              (id >= 12) && (
                <FormattedMessage
                  id={`products.${name}`}
                  defaultMessage={name}
                />
              )
            }
            {
              id < 12 && <>{name}</>
            }
          </span>
          <FormatPrice
            price={discount ? price + discount.price : price}
            minimumFractionDigits={2}
          />
        </Typography>
      ))}
      <Typography
        paragraph
        variant="subtitle2"
      >
        <span className={classes.desc}>
          <FormattedMessage
            id="Total"
            defaultMessage="Total"
          />
        </span>
        <FormatPrice
          price={amount}
          minimumFractionDigits={2}
        />
      </Typography>
      {
        hasTax && (
          <>
            <Divider className={classes.divider} />
            <Typography>
              <span className={classes.desc}>
                <FormattedMessage
                  id="Tax"
                  defaultMessage="Tax"
                />(25%)
              </span>
              <FormatPrice
                price={amount - (amount / 1.25)}
                minimumFractionDigits={2}
              />
            </Typography>
            <Typography>
              <span className={classes.desc}>
                <FormattedMessage
                  id="Netto"
                  defaultMessage="Net"
                />
              </span>
              <FormatPrice
                price={amount - (amount - (amount / 1.25))}
                minimumFractionDigits={2}
              />
            </Typography>
            <Typography
              paragraph
            >
              <span className={classes.desc}>
                <FormattedMessage
                  id="Brutto"
                  defaultMessage="Gross"
                />
              </span>
              <FormatPrice
                price={amount}
                minimumFractionDigits={2}
              />
            </Typography>
          </>
        )
      }

      <Divider className={classes.divider} />
      <Typography paragraph>
        Stugknuten uthyrning AB (556681-5238)<br />
        KIVRA: 556681-5238<br />
        106 31 Stockholm
      </Typography>
      <Typography paragraph>
        <FormattedMessage
          id="Phone"
          defaultMessage="Phone"
        />: 0935 - 101 01
      </Typography>
      <Typography paragraph>
        <FormattedMessage
          id="Email"
          defaultMessage="Email"
        />{': '}
        <a
          href="mailto:info@stugknuten.com"
        >
          info@stugknuten.com
        </a>
      </Typography>
    </Paper>
  );
};

Receipt.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool,
  order: PropTypes.shape({
    id: PropTypes.number,
    amount: PropTypes.number,
    cottage_id: PropTypes.number,
    booking_id: PropTypes.number,
    paid_at: PropTypes.string,
    created_at: PropTypes.string,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        discount: PropTypes.shape({
          price: PropTypes.number,
        }),
        id: PropTypes.number,
        name: PropTypes.string,
        price: PropTypes.number,
      }),
    ),
  }).isRequired,
};

export default withStyles(styles)(Receipt);
